import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, CheckCircle2, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import { companies } from '../data/mockData';
import SEOHead from '../components/SEOHead';

const CompanyPage = () => {
  const { companyId } = useParams();
  const company = companies[companyId];

  // Scroll to top on mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!company) {
    return <Navigate to="/" />;
  }

  // SEO descriptions per company
  const seoDescriptions = {
    immoriya: "immoriya Projektentwicklung GmbH - Die strategische Immobilien-Holding in Osnabrück. Leitung der Unternehmensgruppe mit Energiya, MK und MYNAH.",
    energiya: "Energiya GmbH - Spezialist für energetische Sanierung und Modernisierung in Osnabrück. Wärmepumpen, Dämmung, Photovoltaik und KfW-Förderberatung.",
    mynah: "MYNAH GmbH - Immobilienhandel, Fix & Flip Projekte und Zwangsversteigerungen in Osnabrück. Expertise in Objektbewertung und Projektentwicklung.",
    mk: "MK Immobilienverwaltung GmbH - Verwaltung eigener Wohn- und Gewerbeimmobilien in Osnabrück. Kurzzeitvermietung und Monteurunterkünfte für Firmenkunden."
  };

  return (
    <div className="min-h-screen pt-20">
      <SEOHead 
        title={`${company.name} | immoriya Unternehmensgruppe`}
        description={seoDescriptions[companyId] || company.description}
        canonical={`https://immoriya.de/${companyId}`}
      />

      {/* Hero Section mit Firmenfarbe */}
      <section 
        className="min-h-[80vh] flex items-center justify-center relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${company.color}15 0%, ${company.color}05 100%)` }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 rounded-full blur-3xl"
            style={{ backgroundColor: company.color }}
          />
          <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full blur-3xl"
            style={{ backgroundColor: company.color }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
          <Link to="/" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-8 text-lg transition-colors">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Zurück zur Übersicht
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {company.logo && (
              <img 
                src={company.logo} 
                alt={`${company.name} Logo`}
                className="h-24 md:h-32 mx-auto mb-8 object-contain"
              />
            )}
            
            <span 
              className="px-6 py-3 rounded-full text-white font-semibold text-lg inline-block mb-8"
              style={{ backgroundColor: company.color }}
            >
              {company.ownership}
            </span>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {company.name}
            </h1>

            <p className="text-xl text-gray-600 mb-6 max-w-3xl mx-auto leading-relaxed">
              {company.tagline}
            </p>

            <p className="text-base text-gray-700 max-w-2xl mx-auto leading-relaxed">
              {company.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {company.id === 'energiya' && (
              <div className="mb-6 p-6 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-start space-x-3">
                  <ExternalLink className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Besuchen Sie unsere Website</h4>
                    <p className="text-gray-700 mb-3 leading-relaxed">
                      Weitere Informationen und Details zu unseren Leistungen finden Sie auf unserer Website:
                    </p>
                    <a 
                      href={company.subdomain}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                    >
                      energiya.immoriya.de
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </a>
                  </div>
                </div>
              </div>
            )}

            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
              Unsere Leistungen
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {company.services.map((service, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="flex items-start space-x-3 p-5 rounded-xl hover:shadow-lg transition-shadow"
                  style={{ backgroundColor: `${company.color}05` }}
                >
                  <CheckCircle2 
                    className="w-5 h-5 flex-shrink-0 mt-0.5"
                    style={{ color: company.color }}
                  />
                  <span className="text-base text-gray-800 font-medium">
                    {service}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-2xl shadow-xl p-10"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
              Kontakt
            </h2>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${company.color}20` }}
                >
                  <MapPin className="w-7 h-7" style={{ color: company.color }} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg mb-1">Adresse</h3>
                  <p className="text-gray-600 leading-relaxed">
                    {company.address.street}<br />
                    {company.address.zip} {company.address.city}<br />
                    {company.address.country}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${company.color}20` }}
                >
                  <Phone className="w-7 h-7" style={{ color: company.color }} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg mb-1">Telefon</h3>
                  <a 
                    href={`tel:${company.contact.phone}`}
                    className="text-gray-600 hover:underline text-lg"
                  >
                    {company.contact.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${company.color}20` }}
                >
                  <Mail className="w-7 h-7" style={{ color: company.color }} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg mb-1">E-Mail</h3>
                  <a 
                    href={`mailto:${company.contact.email}`}
                    className="text-gray-600 hover:underline text-lg break-all"
                  >
                    {company.contact.email}
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-gray-200">
              <Link to={`/impressum/${company.id}`} className="text-blue-600 hover:underline font-medium">
                → Impressum {company.name}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section 
        className="py-20 text-white"
        style={{ background: `linear-gradient(135deg, ${company.color} 0%, ${company.color}dd 100%)` }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Haben Sie Fragen?
          </h2>
          <p className="text-lg mb-8 opacity-95">
            Kontaktieren Sie uns gerne für weitere Informationen.
          </p>
          <Link to="/kontakt">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-3 bg-white text-gray-900 text-base font-semibold rounded-full shadow-lg"
            >
              Kontakt
            </motion.button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CompanyPage;
